import * as ToggleGroup from '@studio/design-system/toggle/group';
import {
  DeviceRegistryCommandFunctionType,
  DeviceRegistryCommandKey,
  DeviceRegistryInterfaceItemControlType
} from '@tryiris-ai/common.protojs/api/v1/device_settings';
import type { T_Maybe } from '@tryiris-ai/js-types';
import { noop } from 'lodash-es';
import type { ReactElement } from 'react';

import type { Proxy } from '#~/agent/index.js';
import type { DeepPartial } from '@tryiris-ai/common.protojs';
import type { Control } from '@tryiris-ai/common.protojs/agent/v1/agent';
import type { SettingsStatus } from './status-notifications.js';
import { STATUS_NOTIFICATIONS } from './status-notifications.js';
import type { Device } from '@studio/models/device';

interface DeviceSettingSelectProps {
  agent?: T_Maybe<Proxy>;
  className: string;
  cmdKey: DeviceRegistryCommandKey;
  defaultValue: T_Maybe<number>;
  deviceId: Device['id'];
  disabled?: boolean;
  items: { label: string; value: number }[];
  label: string;
  onMutation: T_Maybe<(mutation: DeepPartial<Control>) => void>;
  onReactiveMutation?: T_Maybe<(mutation: DeepPartial<Control>) => Promise<void>>;
  single: boolean;
  value: number;
  onControlClick?: (controlData: {
    label: string;
    controlType: DeviceRegistryInterfaceItemControlType;
    key: DeviceRegistryCommandKey;
    value?: DeviceRegistryCommandFunctionType;
  }) => void;
  status?: SettingsStatus;
}

/** `<DeviceSettingToggleGroup>` component. */
export const DeviceSettingToggleGroup = ({
  agent,
  className,
  cmdKey,
  defaultValue,
  deviceId,
  disabled,
  items,
  label,
  onMutation,
  onReactiveMutation,
  single,
  value,
  onControlClick,
  status
}: DeviceSettingSelectProps): ReactElement => {
  const mutateToggleGroupValue = async (nextValue: string[] | string): Promise<void> => {
    if (typeof nextValue !== 'string') {
      return;
    }

    const control: DeepPartial<Control> = {
      device: {
        [deviceId]: {
          deviceSettings: {
            [String(cmdKey)]: {
              key: cmdKey,
              select: Number.parseInt(nextValue, 10),
              lastModified: Date.now()
            }
          }
        }
      }
    };

    // Use reactive mutation handler if available, otherwise fall back to legacy behavior
    if (onReactiveMutation) {
      await onReactiveMutation(control);
    } else if (onMutation) {
      onMutation(control);
    } else {
      await agent?.mutate({ control }, {}).catch(noop);
    }

    onControlClick?.({
      label,
      controlType: DeviceRegistryInterfaceItemControlType.SELECT_TOGGLE_GROUP_SINGLE,
      key: cmdKey,
      value: DeviceRegistryCommandFunctionType.VALUE_SET
    });
  };

  return (
    <ToggleGroup.Container
      canRevert={!disabled && defaultValue !== undefined && defaultValue !== value}
      className={className}
      label={label}
      status={status}
      notification={status ? STATUS_NOTIFICATIONS[status] : undefined}
      onRevert={(): void => {
        mutateToggleGroupValue(String(defaultValue!));
      }}
    >
      {!!single && (
        <ToggleGroup.Root
          disabled={!!disabled}
          onValueChange={mutateToggleGroupValue}
          type='single'
          value={String(value)}
        >
          {items.map((item) => (
            <ToggleGroup.Item key={item.value} value={String(item.value)}>
              {item.label}
            </ToggleGroup.Item>
          ))}
        </ToggleGroup.Root>
      )}
      {!single && (
        <ToggleGroup.Root
          disabled={!!disabled}
          onValueChange={noop}
          type='multiple'
          value={[String(value)]}
        >
          {items.map((item) => (
            <ToggleGroup.Item key={item.value} value={String(item.value)}>
              {item.label}
            </ToggleGroup.Item>
          ))}
        </ToggleGroup.Root>
      )}
    </ToggleGroup.Container>
  );
};
